import { useCallback, useEffect, useState } from "react";
import type { GlobalStats, StatsBreakdownRow } from "../types";
import { modelLabel } from "../models";
import { Skeleton } from "./Skeleton";

interface Props {
  onClose: () => void;
}

type PeriodKind = "month" | "30d" | "all" | "custom";

function fmtNum(n: number): string {
  return n.toLocaleString("pt-BR");
}

function fmtTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function fmtDuration(ms: number): string {
  if (ms <= 0) return "—";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}min ${Math.round(s % 60)}s`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function buildStatsUrl(period: PeriodKind, customFrom: string, customTo: string, orgId: string): string {
  const params = new URLSearchParams();
  if (period === "30d") {
    params.set("from", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());
  } else if (period === "all") {
    params.set("from", new Date(0).toISOString());
  } else if (period === "custom" && customFrom) {
    params.set("from", new Date(`${customFrom}T00:00:00`).toISOString());
    params.set("to", new Date(`${customTo || todayIso()}T23:59:59`).toISOString());
  }
  if (orgId) params.set("orgId", orgId);
  const qs = params.toString();
  return qs ? `/api/stats?${qs}` : "/api/stats";
}

function buildStatsCsvUrl(
  period: PeriodKind,
  customFrom: string,
  customTo: string,
  orgId: string,
  by: "user" | "workspace",
): string {
  const statsUrl = buildStatsUrl(period, customFrom, customTo, orgId);
  const query = statsUrl.includes("?") ? statsUrl.slice(statsUrl.indexOf("?")) : "";
  const params = new URLSearchParams(query);
  params.set("by", by);
  return `/api/stats/export.csv?${params.toString()}`;
}

export function StatsPage({ onClose }: Props) {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState<PeriodKind>("month");
  const [customFrom, setCustomFrom] = useState(todayIso());
  const [customTo, setCustomTo] = useState(todayIso());
  const [orgId, setOrgId] = useState("");

  const load = useCallback(() => {
    let alive = true;
    fetch(buildStatsUrl(period, customFrom, customTo, orgId))
      .then((r) => r.json())
      .then((j: GlobalStats) => {
        if (alive) setStats(j);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [period, customFrom, customTo, orgId]);

  useEffect(() => load(), [load]);

  const models = stats
    ? Object.entries(stats.modelUsage).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="about-page" role="dialog" aria-label="Estatísticas de uso">
      <header className="about-header">
        <h1>Estatísticas de uso</h1>
        <button className="about-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </header>

      {stats && (
        <section className="about-section">
          <div className="stats-filters">
            <label className="field" style={{ width: 160 }}>
              <span>Período</span>
              <select value={period} onChange={(e) => setPeriod(e.target.value as PeriodKind)}>
                <option value="month">Mês atual</option>
                <option value="30d">Últimos 30 dias</option>
                <option value="all">Todo o período</option>
                <option value="custom">Personalizado</option>
              </select>
            </label>
            {period === "custom" && (
              <>
                <label className="field" style={{ width: 150 }}>
                  <span>De</span>
                  <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                </label>
                <label className="field" style={{ width: 150 }}>
                  <span>Até</span>
                  <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </label>
              </>
            )}
            {stats.availableOrgs && stats.availableOrgs.length > 0 && (
              <label className="field" style={{ width: 200 }}>
                <span>Organização</span>
                <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                  <option value="">(minha org ativa)</option>
                  {stats.availableOrgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </section>
      )}

      {error ? (
        <section className="about-section">
          <p>Não consegui carregar as estatísticas.</p>
        </section>
      ) : !stats ? (
        <section className="about-section">
          <div className="stats-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="stat-card" key={i}>
                <Skeleton width="60%" height={22} />
                <Skeleton width="45%" height={11} style={{ marginTop: 8 }} />
                <Skeleton width="35%" height={9} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        </section>
      ) : (
        <>
          <section className="about-section">
            <div className="stats-grid">
              <StatCard label="Conversas" value={fmtNum(stats.sessionCount)} hint={`${stats.pinnedCount} fixadas`} />
              <StatCard label="Mensagens" value={fmtNum(stats.totalUserTurns)} hint="perguntas suas" />
              <StatCard label="Respostas" value={fmtNum(stats.totalAssistantTurns)} hint={`${stats.errored} com erro`} />
              <StatCard label="Tokens (total)" value={fmtTokens(stats.totalTokens)} hint={`${fmtTokens(stats.outputTokens)} de saída`} />
              <StatCard label="Cache lido" value={fmtTokens(stats.cacheReadTokens)} hint="tokens reaproveitados" />
              <StatCard label="Custo no período" value={`$${stats.totalCostUsd.toFixed(4)}`} hint="aproximado" />
              <StatCard label="Tempo total" value={fmtDuration(stats.totalDurationMs)} hint={`média ${fmtDuration(stats.avgDurationMs)}`} />
              <StatCard label="Feedback" value={`👍 ${stats.likes} · 👎 ${stats.dislikes}`} hint="suas avaliações" />
            </div>
          </section>

          <section className="about-section">
            <h2>Origem das conversas</h2>
            <div className="stats-grid">
              <StatCard
                label="Manuais (painel)"
                value={fmtNum(stats.bySource.web.sessionCount)}
                hint={`${fmtTokens(stats.bySource.web.tokens)} tokens · $${stats.bySource.web.costUsd.toFixed(4)}`}
              />
              <StatCard
                label="Via API"
                value={fmtNum(stats.bySource.api.sessionCount)}
                hint={`${fmtTokens(stats.bySource.api.tokens)} tokens · $${stats.bySource.api.costUsd.toFixed(4)}`}
              />
            </div>
          </section>

          {stats.byAgent && (
            <section className="about-section">
              <h2>Por motor de IA</h2>
              <div className="stats-grid">
                <StatCard
                  label="Claude"
                  value={`$${stats.byAgent.claude.costUsd.toFixed(4)}`}
                  hint={`${fmtNum(stats.byAgent.claude.responses)} respostas · ${fmtTokens(stats.byAgent.claude.tokens)} tokens`}
                />
                <StatCard
                  label="Codex"
                  value={`$${stats.byAgent.codex.costUsd.toFixed(4)}`}
                  hint={`${fmtNum(stats.byAgent.codex.responses)} respostas · ${fmtTokens(stats.byAgent.codex.tokens)} tokens`}
                />
              </div>
            </section>
          )}

          {stats.plan && (
            <section className="about-section">
              <h2>Plano e limites</h2>
              <div className="stats-grid">
                <StatCard
                  label="Plano"
                  value={stats.plan.linked ? (stats.plan.planId ?? "vinculado") : "sem vínculo"}
                  hint={stats.plan.linked ? `status: ${stats.plan.status ?? "?"}` : "usando limites da plataforma"}
                />
                <StatCard
                  label="Assentos"
                  value={`${stats.plan.seats.count}${stats.plan.seats.limit != null ? ` / ${stats.plan.seats.limit}` : ""}`}
                  hint="contas aprovadas"
                />
                <StatCard
                  label="Workspaces"
                  value={`${stats.plan.workspaces.count}${stats.plan.workspaces.limit != null ? ` / ${stats.plan.workspaces.limit}` : ""}`}
                  hint={`${stats.plan.repoCount} repositório(s) conectado(s)`}
                />
                <StatCard
                  label="Orçamento de IA"
                  value={stats.plan.budgetUsdMonthly != null ? `US$ ${stats.plan.budgetUsdMonthly.toFixed(2)}/mês` : "ilimitado"}
                  hint="teto mensal da organização"
                />
                <StatCard
                  label="Sincronização"
                  value={`a cada ${stats.plan.repoSyncMinutes} min`}
                  hint="auto-sync dos repositórios"
                />
              </div>
            </section>
          )}

          {stats.budget && (
            <section className="about-section">
              <BudgetCard budget={stats.budget} />
            </section>
          )}

          {stats.breakdown && (stats.breakdown.byWorkspace.length > 0 || stats.breakdown.byUser.length > 0) && (
            <section className="about-section">
              <div className="stats-export-row">
                <span className="admin-user-meta">Baixar este período como planilha:</span>
                <a className="btn-approve" href={buildStatsCsvUrl(period, customFrom, customTo, orgId, "user")} download>
                  CSV por usuário
                </a>
                <a className="btn-approve" href={buildStatsCsvUrl(period, customFrom, customTo, orgId, "workspace")} download>
                  CSV por workspace
                </a>
              </div>
              <div className="stats-breakdown-grid">
                {stats.breakdown.byWorkspace.length > 0 && (
                  <BreakdownChart
                    title="Custo por workspace"
                    rows={stats.breakdown.byWorkspace.map((r) => ({ ...r, label: r.workspaceName }))}
                  />
                )}
                {stats.breakdown.byUser.length > 0 && (
                  <BreakdownChart
                    title="Custo por usuário"
                    rows={stats.breakdown.byUser.map((r) => ({ ...r, label: r.userEmail }))}
                  />
                )}
              </div>
            </section>
          )}

          {models.length > 0 && (
            <section className="about-section">
              <h2>Modelos usados</h2>
              <ul className="about-list">
                {models.map(([id, count]) => (
                  <li key={id}>
                    <strong>{modelLabel(id)}</strong> — {count} conversa{count === 1 ? "" : "s"}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="about-section">
            <p className="dim">
              Números do período selecionado, escopados à organização ativa. Use “Exportar backup” na barra
              lateral para baixar tudo em JSON.
            </p>
          </section>
        </>
      )}

      <footer className="about-footer">
        <button className="about-back" onClick={onClose}>
          Voltar à conversa
        </button>
      </footer>
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
      {hint && <div className="stat-card-hint">{hint}</div>}
    </div>
  );
}

const BREAKDOWN_TOP_N = 8;

function BreakdownChart({ title, rows }: { title: string; rows: (StatsBreakdownRow & { label: string })[] }) {
  const top = rows.slice(0, BREAKDOWN_TOP_N);
  const rest = rows.slice(BREAKDOWN_TOP_N);
  const bars =
    rest.length > 0
      ? [
          ...top,
          {
            label: `outros (${rest.length})`,
            costUsd: rest.reduce((acc, r) => acc + r.costUsd, 0),
            tokens: rest.reduce((acc, r) => acc + r.tokens, 0),
            sessionCount: rest.reduce((acc, r) => acc + r.sessionCount, 0),
          },
        ]
      : top;
  const max = Math.max(...bars.map((b) => b.costUsd), 0.0001);

  return (
    <div className="stats-breakdown">
      <h3>{title}</h3>
      {bars.map((b, i) => (
        <div className="stats-bar-row" key={i}>
          <span className="stats-bar-label" title={b.label}>{b.label}</span>
          <div className="stats-bar-track">
            <div className="stats-bar-fill" style={{ width: `${Math.max((b.costUsd / max) * 100, 1)}%` }} />
          </div>
          <span className="stats-bar-value">${b.costUsd.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

function BudgetCard({ budget }: { budget: NonNullable<GlobalStats["budget"]> }) {
  const { limitUsdMonthly, spentUsdThisMonth, projectedUsdThisMonth } = budget;
  const ratio = limitUsdMonthly ? Math.min(spentUsdThisMonth / limitUsdMonthly, 1) : 0;
  const projectedRatio = limitUsdMonthly ? Math.min(projectedUsdThisMonth / limitUsdMonthly, 1) : 0;
  const barClass = projectedRatio >= 1 ? "danger" : projectedRatio >= 0.8 ? "warn" : "";

  return (
    <div className="stats-budget-card">
      <h3>Orçamento de IA — mês corrente</h3>
      <div>
        US$ {spentUsdThisMonth.toFixed(2)} gastos
        {limitUsdMonthly === null ? " (sem limite definido)" : ` de US$ ${limitUsdMonthly.toFixed(2)}`}
      </div>
      {limitUsdMonthly !== null && (
        <div className="stats-progress-track">
          <div className={`stats-progress-fill ${barClass}`} style={{ width: `${ratio * 100}%` }} />
        </div>
      )}
      <div className="stat-card-hint">
        Projeção estimada pro fim do mês: <strong>US$ {projectedUsdThisMonth.toFixed(2)}</strong>
        {limitUsdMonthly !== null &&
          (projectedUsdThisMonth > limitUsdMonthly
            ? " — acima do orçamento no ritmo atual"
            : " — dentro do orçamento no ritmo atual")}
      </div>
    </div>
  );
}
